import { useEffect, useRef, useState } from 'react'

import { canPreviewFileAttachment, extractExtensionOfFileName } from 'utils/commonFunctions'

import type { AttachmentPreviewPageState } from '.'

export type UseAttachmentBlobPreviewReturn = {
  pageState: AttachmentPreviewPageState
  fileUrl: string
  fileName: string
  isPdf: boolean
  isPreviewable: boolean
}

/**
 * 添付ファイルを Blob で取得し、プレビュー用の objectURL を生成・解放する共有 hook。
 *
 * ファイルの取得方法（認証方式・エンドポイント・パラメータ）は呼び出し側が `fetchBlob`
 * （メモ化済みであること）として注入する。ドライバー向け（SP）と出荷元担当者向け
 * （PC 納品実績詳細）でロジックを共有するために切り出している。
 *
 * @param fileName プレビュー対象のファイル名（拡張子判定・表示に使う）
 * @param fetchBlob Blob を取得する関数。`useCallback` でメモ化して渡すこと
 * @param enabled 取得可能な状態か。false の場合は取得せず error 扱いにする（認証情報未取得など）
 */
export const useAttachmentBlobPreview = (
  fileName: string,
  fetchBlob: () => Promise<Blob>,
  enabled = true
): UseAttachmentBlobPreviewReturn => {
  const [pageState, setPageState] = useState<AttachmentPreviewPageState>('loading')
  const [fileUrl, setFileUrl] = useState('')
  // 解放用に最新の objectURL を保持する
  const fileUrlRef = useRef('')

  const extension = extractExtensionOfFileName(fileName)
  const isPdf = extension === 'pdf'
  const isPreviewable = canPreviewFileAttachment(fileName)

  useEffect(() => {
    if (!enabled) {
      setPageState('error')
      return
    }
    let isMounted = true
    const fetchFile = async () => {
      try {
        const blob = await fetchBlob()
        if (!isMounted) return
        // BE は application/octet-stream 固定で返すため、PDF は明示的に type を付け直す
        const typed = isPdf ? new Blob([blob], { type: 'application/pdf' }) : blob
        const url = URL.createObjectURL(typed)
        fileUrlRef.current = url
        setFileUrl(url)
        setPageState('ready')
      } catch {
        if (isMounted) setPageState('error')
      }
    }
    fetchFile()
    return () => {
      isMounted = false
      if (fileUrlRef.current) URL.revokeObjectURL(fileUrlRef.current)
    }
  }, [fetchBlob, isPdf, enabled])

  return {
    pageState,
    fileUrl,
    fileName,
    isPdf,
    isPreviewable,
  }
}
